// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { CodeSystem, CodeSystemConcept, HealthcareService, Resource } from '@medplum/fhirtypes';
import { APPOINTMENT_TYPE_SYSTEM, COOLDOWN_BUFFER_MINUTES, parseAppointmentType } from '@polarissleep/scheduling';
import { MAX_PROJECTION_SEARCH_COUNT, POLARIS_ENV_TAG_SYSTEM, SCHEDULING_PARAMETERS_EXTENSION_URL } from './constants';
import { validateSchedulingScope, withTenantAndEnvironmentScope } from './tenant';
import type { SchedulingScope } from './types';

/** The existing Polaris appointment-type CodeSystem remains the source of truth. */
export const POLARIS_APPOINTMENT_TYPE_SYSTEM = APPOINTMENT_TYPE_SYSTEM;

/** Stable identifier for the one v2 HealthcareService projected from an appointment type. */
export const APPOINTMENT_TYPE_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM =
  'https://polarissleep.com/healthcare-service/appointment-type';

/** Projection-owned extension carrying metadata that FHIR scheduling does not model. */
export const POLARIS_APPOINTMENT_TYPE_METADATA_EXTENSION_URL =
  'http://polarissleep.com/StructureDefinition/appointment-type-metadata';

const METADATA_EXTENSION_URLS = new Set([POLARIS_APPOINTMENT_TYPE_METADATA_EXTENSION_URL]);
const PROJECTION_EXTENSION_URLS = new Set([SCHEDULING_PARAMETERS_EXTENSION_URL, ...METADATA_EXTENSION_URLS]);
const UCUM_SYSTEM = 'http://unitsofmeasure.org';

export interface AppointmentTypeHealthcareServiceClient {
  searchResources(resourceType: 'HealthcareService', search: Record<string, string>): Promise<HealthcareService[]>;
  searchResourcePages?: (
    resourceType: 'HealthcareService',
    search: Record<string, string>
  ) => AsyncIterable<HealthcareService[]>;
  createResource(resource: HealthcareService): Promise<HealthcareService>;
  updateResource(resource: HealthcareService): Promise<HealthcareService>;
  createResourceIfNoneExist?: (resource: HealthcareService, query: string) => Promise<HealthcareService>;
}

export interface AppointmentTypeProjectionReportItem {
  code: string;
  status: 'missing' | 'duplicate' | 'mismatched' | 'unchanged' | 'invalid' | 'skipped' | 'deactivated';
  action: 'create' | 'update' | 'none';
  resourceIds: string[];
  detail: string;
}

export interface AppointmentTypeProjectionReport {
  dryRun: boolean;
  items: AppointmentTypeProjectionReportItem[];
  counts: {
    missing: number;
    duplicates: number;
    mismatched: number;
    unchanged: number;
    invalid: number;
    skipped: number;
    continuations: number;
    created: number;
    updated: number;
    deactivated: number;
  };
}

interface ParsedAppointmentType {
  code: string;
  display: string;
  definition?: string;
  durationMinutes: number;
  billingCode: string;
  billingModifier: string;
  providerTypes: string[];
  preferredProvider?: string;
  patientStatus: string;
  patientEligibility: string;
  mode: string;
  visibleInPortal: boolean;
  placeOfServiceCode: string;
  explicitlySchedulable: boolean;
}

export function getAppointmentTypeHealthcareServiceIdentifier(code: string): {
  system: string;
  value: string;
} {
  const normalizedCode = code.trim();
  if (!normalizedCode) {
    throw new Error('Appointment type code is required for HealthcareService projection');
  }
  return { system: APPOINTMENT_TYPE_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM, value: normalizedCode };
}

/**
 * Builds the projection without an id. The resource ID is intentionally left to Medplum;
 * the stable identifier is the appointment type code, scoped by tenant and environment.
 */
export function buildAppointmentTypeHealthcareService(
  concept: CodeSystemConcept,
  scope: SchedulingScope
): HealthcareService | undefined {
  const appointmentType = parseAppointmentTypeConcept(concept);
  if (!appointmentType.explicitlySchedulable) {
    return undefined;
  }
  if (!appointmentType.code) {
    throw new Error('Schedulable appointment type is missing a code');
  }
  if (!Number.isInteger(appointmentType.durationMinutes) || appointmentType.durationMinutes <= 0) {
    throw new Error(`Appointment type ${appointmentType.code} must have a positive integer duration`);
  }

  const effectiveCadenceMinutes = appointmentType.durationMinutes + COOLDOWN_BUFFER_MINUTES;
  const resource: HealthcareService = {
    resourceType: 'HealthcareService',
    identifier: [{ use: 'official', ...getAppointmentTypeHealthcareServiceIdentifier(appointmentType.code) }],
    active: true,
    type: [
      {
        coding: [
          {
            system: POLARIS_APPOINTMENT_TYPE_SYSTEM,
            code: appointmentType.code,
            display: appointmentType.display,
          },
        ],
      },
    ],
    name: appointmentType.display,
    comment: appointmentType.definition,
    appointmentRequired: true,
    extension: [
      {
        url: SCHEDULING_PARAMETERS_EXTENSION_URL,
        extension: [
          { url: 'duration', valueDuration: minutes(appointmentType.durationMinutes) },
          { url: 'bufferBefore', valueDuration: minutes(0) },
          { url: 'bufferAfter', valueDuration: minutes(COOLDOWN_BUFFER_MINUTES) },
          { url: 'alignmentInterval', valueDuration: minutes(effectiveCadenceMinutes) },
          // The alignment grid is local to the actor's Schedule in Phase 2; HealthcareService
          // contributes the explicit zero offset but does not invent a global timezone origin.
          { url: 'alignmentOffset', valueDuration: minutes(0) },
        ],
      },
      buildMetadataExtension(appointmentType),
    ],
  };

  return withTenantAndEnvironmentScope(resource, scope.organizationId, scope.environment);
}

export async function reconcileAppointmentTypeHealthcareServices(
  client: AppointmentTypeHealthcareServiceClient,
  codeSystem: CodeSystem,
  scope: SchedulingScope,
  options: { dryRun?: boolean } = {}
): Promise<AppointmentTypeProjectionReport> {
  validateSchedulingScope(scope);
  if (codeSystem.url !== POLARIS_APPOINTMENT_TYPE_SYSTEM) {
    throw new Error(`Expected CodeSystem ${POLARIS_APPOINTMENT_TYPE_SYSTEM}`);
  }
  if (codeSystem.status !== 'active') {
    throw new Error('Appointment type CodeSystem must be active before projecting HealthcareServices');
  }

  const dryRun = options.dryRun ?? true;
  const report: AppointmentTypeProjectionReport = {
    dryRun,
    items: [],
    counts: {
      missing: 0,
      duplicates: 0,
      mismatched: 0,
      unchanged: 0,
      invalid: 0,
      skipped: 0,
      continuations: 0,
      created: 0,
      updated: 0,
      deactivated: 0,
    },
  };

  const conceptCodeCounts = new Map<string, number>();
  for (const concept of codeSystem.concept ?? []) {
    const code = concept.code?.trim() ?? '';
    conceptCodeCounts.set(code, (conceptCodeCounts.get(code) ?? 0) + 1);
  }

  for (const concept of codeSystem.concept ?? []) {
    const code = concept.code?.trim() ?? '';
    if ((conceptCodeCounts.get(code) ?? 0) > 1) {
      report.counts.invalid++;
      report.items.push({
        code,
        status: 'invalid',
        action: 'none',
        resourceIds: [],
        detail: `Duplicate CodeSystem concept code "${code}"; no HealthcareService projection was attempted.`,
      });
      continue;
    }

    let expected: HealthcareService | undefined;
    try {
      expected = buildAppointmentTypeHealthcareService(concept, scope);
    } catch (error) {
      report.counts.invalid++;
      report.items.push({
        code,
        status: 'invalid',
        action: 'none',
        resourceIds: [],
        detail: error instanceof Error ? error.message : String(error),
      });
      continue;
    }
    if (!expected) {
      try {
        getAppointmentTypeHealthcareServiceIdentifier(code);
      } catch (error) {
        report.counts.invalid++;
        report.items.push({
          code,
          status: 'invalid',
          action: 'none',
          resourceIds: [],
          detail: error instanceof Error ? error.message : String(error),
        });
        continue;
      }
    }

    const identifier = getAppointmentTypeHealthcareServiceIdentifier(code);
    const search = {
      identifier: `${identifier.system}|${identifier.value}`,
      _compartment: `Organization/${scope.organizationId}`,
      _tag: `${POLARIS_ENV_TAG_SYSTEM}|${scope.environment}`,
      _count: String(MAX_PROJECTION_SEARCH_COUNT),
    };
    const { resources: sameIdentifierResources, complete } = await searchAllIdentifierCandidates(client, search);

    if (!complete) {
      report.counts.invalid++;
      report.counts.continuations++;
      report.items.push({
        code,
        status: 'invalid',
        action: 'none',
        resourceIds: sameIdentifierResources.flatMap((resource) => (resource.id ? [resource.id] : [])),
        detail: `Search returned the maximum bounded page (${MAX_PROJECTION_SEARCH_COUNT}); no create or update was attempted because continuation could not be verified.`,
      });
      continue;
    }

    const sameIdentifierCandidates = sameIdentifierResources.filter(
      (resource) => hasMatchingIdentifier(resource, identifier) && hasTargetScope(resource, scope)
    );
    const invalidCandidates = sameIdentifierCandidates.filter(
      (resource) => !isManagedCandidate(resource, identifier, scope)
    );

    if (invalidCandidates.length > 0) {
      report.counts.invalid++;
      report.items.push({
        code,
        status: 'invalid',
        action: 'none',
        resourceIds: invalidCandidates.flatMap((resource) => (resource.id ? [resource.id] : [])),
        detail:
          'Same-identifier HealthcareService resources have malformed or wrong tenant/environment scope; no resource was changed.',
      });
      continue;
    }

    const candidates = sameIdentifierCandidates;

    if (candidates.length > 1) {
      report.counts.duplicates++;
      report.items.push({
        code,
        status: 'duplicate',
        action: 'none',
        resourceIds: candidates.flatMap((resource) => (resource.id ? [resource.id] : [])),
        detail: 'Duplicate scoped HealthcareServices found; no resource was changed.',
      });
      continue;
    }

    if (!expected) {
      const existing = candidates[0];
      if (!existing) {
        report.counts.skipped++;
        report.items.push({
          code,
          status: 'skipped',
          action: 'none',
          resourceIds: [],
          detail: 'Concept is explicitly marked schedulable=false; no scoped HealthcareService exists.',
        });
        continue;
      }

      if (existing.active === false) {
        report.counts.skipped++;
        report.items.push({
          code,
          status: 'skipped',
          action: 'none',
          resourceIds: existing.id ? [existing.id] : [],
          detail: 'Concept is explicitly marked schedulable=false; HealthcareService is already inactive.',
        });
        continue;
      }

      report.counts.deactivated++;
      if (!dryRun) {
        await client.updateResource({ ...existing, active: false });
      }
      report.items.push({
        code,
        status: 'deactivated',
        action: 'update',
        resourceIds: existing.id ? [existing.id] : [],
        detail: dryRun
          ? 'Would deactivate the scoped HealthcareService for the non-schedulable concept.'
          : 'Deactivated the scoped HealthcareService for the non-schedulable concept.',
      });
      continue;
    }

    const existing = candidates[0];
    if (!existing) {
      report.counts.missing++;
      let resourceIds: string[] = [];
      if (!dryRun) {
        const created = client.createResourceIfNoneExist
          ? await client.createResourceIfNoneExist(
              expected,
              `identifier=${identifier.system}|${identifier.value}&_compartment=Organization/${scope.organizationId}&_tag=${POLARIS_ENV_TAG_SYSTEM}|${scope.environment}`
            )
          : await client.createResource(expected);
        if (isManagedCandidate(created, identifier, scope)) {
          report.counts.created++;
          resourceIds = created.id ? [created.id] : [];
        } else {
          report.counts.invalid++;
          report.items.push({
            code,
            status: 'invalid',
            action: 'none',
            resourceIds: created.id ? [created.id] : [],
            detail:
              'Create guard returned a malformed or wrong-scope same-identifier HealthcareService; no duplicate was accepted.',
          });
          continue;
        }
      }
      report.items.push({
        code,
        status: 'missing',
        action: 'create',
        resourceIds,
        detail: dryRun ? 'Would create the projected HealthcareService.' : 'Created the projected HealthcareService.',
      });
      continue;
    }

    if (!isProjectionMatch(existing, expected)) {
      report.counts.mismatched++;
      if (!dryRun) {
        await client.updateResource(mergeProjection(existing, expected));
        report.counts.updated++;
      }
      report.items.push({
        code,
        status: 'mismatched',
        action: 'update',
        resourceIds: existing.id ? [existing.id] : [],
        detail: dryRun ? 'Would update the mismatched projected fields.' : 'Updated the mismatched projected fields.',
      });
      continue;
    }

    report.counts.unchanged++;
    report.items.push({
      code,
      status: 'unchanged',
      action: 'none',
      resourceIds: existing.id ? [existing.id] : [],
      detail: 'Projected HealthcareService already matches the source configuration.',
    });
  }

  return report;
}

function parseAppointmentTypeConcept(concept: CodeSystemConcept): ParsedAppointmentType {
  const appointmentType = parseAppointmentType(concept);
  const schedulable = concept.property?.find((property) => property.code === 'schedulable')?.valueString;

  return {
    code: appointmentType.coding.code?.trim() ?? '',
    display: appointmentType.display,
    definition: concept.definition,
    durationMinutes: appointmentType.durationMinutes,
    billingCode: appointmentType.billingCode,
    billingModifier: appointmentType.billingModifier,
    providerTypes: appointmentType.providerTypes,
    preferredProvider: appointmentType.preferredProvider,
    patientStatus: appointmentType.patientStatus,
    patientEligibility: appointmentType.patientEligibility,
    mode: appointmentType.mode,
    visibleInPortal: appointmentType.visibleInPortal,
    placeOfServiceCode: appointmentType.placeOfServiceCode,
    explicitlySchedulable: schedulable !== 'false',
  };
}

function buildMetadataExtension(appointmentType: ParsedAppointmentType) {
  const extension = [
    ...(appointmentType.billingCode ? [{ url: 'billingCode', valueString: appointmentType.billingCode }] : []),
    ...(appointmentType.billingModifier
      ? [{ url: 'billingModifier', valueString: appointmentType.billingModifier }]
      : []),
    ...appointmentType.providerTypes.map((providerType) => ({ url: 'providerType', valueCode: providerType })),
    ...(appointmentType.patientStatus ? [{ url: 'patientStatus', valueCode: appointmentType.patientStatus }] : []),
    ...(appointmentType.patientEligibility
      ? [{ url: 'patientEligibility', valueString: appointmentType.patientEligibility }]
      : []),
    ...(appointmentType.mode ? [{ url: 'mode', valueCode: appointmentType.mode }] : []),
    { url: 'visibleInPortal', valueBoolean: appointmentType.visibleInPortal },
    ...(appointmentType.placeOfServiceCode
      ? [{ url: 'placeOfServiceCode', valueCode: appointmentType.placeOfServiceCode }]
      : []),
  ];
  if (appointmentType.preferredProvider) {
    extension.push({ url: 'preferredProvider', valueCode: appointmentType.preferredProvider });
  }
  return { url: POLARIS_APPOINTMENT_TYPE_METADATA_EXTENSION_URL, extension };
}

function minutes(value: number) {
  return { value, unit: 'min', system: UCUM_SYSTEM, code: 'min' };
}

function isManagedCandidate(
  resource: HealthcareService,
  identifier: { system: string; value: string },
  scope: SchedulingScope
): boolean {
  return (
    resource.resourceType === 'HealthcareService' &&
    resource.id?.trim() !== '' &&
    resource.identifier?.filter((item) => item.system === identifier.system && item.value === identifier.value)
      .length === 1 &&
    hasTargetScope(resource, scope) &&
    resource.meta?.tag?.filter((tag) => tag.system === POLARIS_ENV_TAG_SYSTEM).length === 1
  );
}

function hasTargetScope(resource: HealthcareService, scope: SchedulingScope): boolean {
  return (
    resource.meta?.account?.reference === `Organization/${scope.organizationId}` &&
    resource.meta?.tag?.some((tag) => tag.system === POLARIS_ENV_TAG_SYSTEM && tag.code === scope.environment) === true
  );
}

function hasMatchingIdentifier(resource: HealthcareService, identifier: { system: string; value: string }): boolean {
  return (
    resource.identifier?.some((item) => item.system === identifier.system && item.value === identifier.value) === true
  );
}

async function searchAllIdentifierCandidates(
  client: AppointmentTypeHealthcareServiceClient,
  search: Record<string, string>
): Promise<{ resources: HealthcareService[]; complete: boolean }> {
  if (client.searchResourcePages) {
    const resources: HealthcareService[] = [];
    for await (const page of client.searchResourcePages('HealthcareService', search)) {
      resources.push(...page);
    }
    return { resources, complete: true };
  }

  const resources = await client.searchResources('HealthcareService', search);
  return { resources, complete: resources.length < MAX_PROJECTION_SEARCH_COUNT };
}

function isProjectionMatch(existing: HealthcareService, expected: HealthcareService): boolean {
  return (
    canonical({
      identifier: existing.identifier?.filter(
        (item) => item.system === APPOINTMENT_TYPE_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM
      ),
      active: existing.active,
      type: existing.type,
      name: existing.name,
      comment: existing.comment,
      appointmentRequired: existing.appointmentRequired,
      extension: existing.extension?.filter((extension) => PROJECTION_EXTENSION_URLS.has(extension.url)),
      account: existing.meta?.account,
      environment: existing.meta?.tag?.filter((tag) => tag.system === POLARIS_ENV_TAG_SYSTEM),
    }) ===
    canonical({
      identifier: expected.identifier,
      active: expected.active,
      type: expected.type,
      name: expected.name,
      comment: expected.comment,
      appointmentRequired: expected.appointmentRequired,
      extension: expected.extension,
      account: expected.meta?.account,
      environment: expected.meta?.tag?.filter((tag) => tag.system === POLARIS_ENV_TAG_SYSTEM),
    })
  );
}

function mergeProjection(existing: HealthcareService, expected: HealthcareService): HealthcareService {
  const existingIdentifiers = (existing.identifier ?? []).filter(
    (item) => item.system !== APPOINTMENT_TYPE_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM
  );
  const existingExtensions = (existing.extension ?? []).filter(
    (extension) => !PROJECTION_EXTENSION_URLS.has(extension.url)
  );
  const existingTags = (existing.meta?.tag ?? []).filter((tag) => tag.system !== POLARIS_ENV_TAG_SYSTEM);

  return {
    ...existing,
    ...expected,
    id: existing.id,
    identifier: [...existingIdentifiers, ...(expected.identifier ?? [])],
    extension: [...existingExtensions, ...(expected.extension ?? [])],
    meta: {
      ...existing.meta,
      ...expected.meta,
      account: expected.meta?.account,
      tag: [...existingTags, ...(expected.meta?.tag ?? [])],
    },
  };
}

function canonical(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(canonical).sort().join(',')}]`;
  }
  if (value && typeof value === 'object') {
    return `{${Object.entries(value as Record<string, unknown>)
      .filter(([, item]) => item !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => `${JSON.stringify(key)}:${canonical(item)}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

export function isHealthcareServiceProjectionResource(resource: Resource): resource is HealthcareService {
  return (
    resource.resourceType === 'HealthcareService' &&
    resource.identifier?.some(
      (identifier) => identifier.system === APPOINTMENT_TYPE_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM
    ) === true
  );
}
